import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { PATH } from '../../../constants/router'
import { setPartnerVehicleResult } from '../../../helper/partnerSessionStore'
import { formatDateNoTime } from '../utils/formatDate'
import EmptyRecentCard from './EmptyRecentCard'
import { SwipeToDeleteRow } from './SwipeToDeleteRow'
import trafficFineBell from '../../../assets/images/traffic_fine_bell.png'

const DEFAULT_VISIBLE = 3

function ActionPill({ bgClass, left, right, onRight, leftColorClass = '', rightColorClass = '' }) {
  return (
    <div className={`traffic-fine-action-pill ${bgClass}`}>
      <div className={`traffic-fine-action-pill-left ${leftColorClass}`}>{left}</div>
      <button
        type="button"
        className={`traffic-fine-action-pill-right ${rightColorClass}`}
        onClick={(e) => {
          e.stopPropagation()
          onRight?.()
        }}
        onPointerDown={(e) => {
          e.stopPropagation()
        }}>
        {right}
      </button>
    </div>
  )
}

function ProductPill({ label }) {
  if (!label) return null

  return (
    <div className="traffic-fine-product-pill">
      <img src={trafficFineBell} alt="" />
      <span>{label}</span>
    </div>
  )
}

export default function RecentVehicleList({
  phoneNumber = '',
  items = [],
  vehicleTypeIconMap,
  vehicleTypesByValue,
  onSelect,
  onDelete,
  selectedKey,
  onToggleSelect,
  onClearInput
}) {
  const [expanded, setExpanded] = useState(false)
  const history = useHistory()

  const safeItems = useMemo(() => {
    const incoming = (items || []).filter((x) => x?.plate)
    const seen = new Set()
    const unique = []

    for (const it of incoming) {
      const key = `${it.plate}-${Number(it.vehicleType)}`
      if (seen.has(key)) continue
      seen.add(key)
      unique.push(it)
    }
    return unique
  }, [items])

  if (!safeItems.length) {
    return (
      <div className="traffic-fine-recent-wrapper">
        <EmptyRecentCard />
      </div>
    )
  }

  const canToggle = safeItems.length > DEFAULT_VISIBLE
  const visibleItems = expanded ? safeItems : safeItems.slice(0, DEFAULT_VISIBLE)
  const hiddenCount = Math.max(safeItems.length - DEFAULT_VISIBLE, 0)

  return (
    <div className="traffic-fine-recent-wrapper">
      <div className="traffic-fine-recent-title">Xe của bạn ({safeItems.length})</div>

      <div className="traffic-fine-recent-list-container">
        <div className="traffic-fine-recent-list">
          {visibleItems.map((item) => {
            const icon = vehicleTypeIconMap?.[item.vehicleType]
            const label = vehicleTypesByValue?.[item.vehicleType] || 'Xe'
            const rowKey = `${item.plate}-${item.vehicleType}`
            const isSelected = selectedKey === rowKey

            const handleClick = () => {
              if (isSelected) {
                onToggleSelect?.(null)
                onClearInput?.()
                return
              }
              onToggleSelect?.(rowKey)
              onSelect?.(item)
            }

            const idxRaw = item?.activeNotifyPackageIndex
            const idx = idxRaw === null || idxRaw === undefined || idxRaw === '' ? null : Number(idxRaw)
            const pkgs = Array.isArray(item?.customerSchedule) ? item.customerSchedule : []
            const pkg = Number.isInteger(idx) && idx >= 0 && idx < pkgs.length ? pkgs[idx] : null

            const expire = pkg?.notifyEndDateTime || pkg?.notifyEndDate || ''
            const productLabel = pkg?.productName || null
            const showAutoRenew = !!pkg
            const showNotifyRegister = !pkg
            const allowRenew01 = Number(pkg?.allowRenew ?? 1)
            const leftText = allowRenew01 === 1 ? `Gia hạn tự động vào ${formatDateNoTime(expire)}` : `Hết hạn vào ${formatDateNoTime(expire)}`

            return (
              <SwipeToDeleteRow
                key={rowKey}
                onClick={handleClick}
                onDelete={() => onDelete?.(item)}
                selected={isSelected}
                checkIcon={
                  <div className="traffic-fine-selected-icon">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                      <path d="M5 12.5L10 17L19 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                }>
                <div className="traffic-fine-row-body">
                  <div className="traffic-fine-row-top">
                    {icon ? <img src={icon} alt={label} className="traffic-fine-row-type-icon" /> : null}

                    <div className="traffic-fine-row-main">
                      <div className="traffic-fine-row-plate-group">
                        <div className="traffic-fine-row-plate">{item.plate}</div>
                        {productLabel ? <ProductPill label={productLabel} /> : null}
                      </div>
                    </div>

                    <div className="traffic-fine-row-chevron">›</div>
                  </div>

                  {showAutoRenew ? (
                    <ActionPill
                      bgClass="traffic-fine-action-pill--brand-soft"
                      left={leftText}
                      right="Quản lý"
                      onRight={() => {
                        setPartnerVehicleResult({
                          plate: item.vehicleIdentity || item.plate,
                          vehicleType: Number(item.vehicleType),
                          activePkg: pkg
                            ? {
                                notifyInfoId: pkg.appUserVehicleNotifyInfoId,
                                productId: pkg.productId,
                                productName: pkg.productName,
                                allowRenew: pkg.allowRenew,
                                notifyStartDateTime: pkg.notifyStartDateTime,
                                notifyEndDateTime: pkg.notifyEndDateTime || pkg.notifyEndDate,
                                appUserVehicleId: pkg.appUserVehicleId
                              }
                            : null
                        })

                        history.push(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_PACKAGE_INFO)
                      }}
                    />
                  ) : null}

                  {showNotifyRegister ? (
                    <ActionPill
                      bgClass="traffic-fine-action-pill--gray"
                      left="Nhận thông báo tự động khi có lỗi"
                      right="Đăng ký"
                      onRight={() => {
                        const pn = String(phoneNumber || '').trim()
                        const query = pn ? `?phoneNumber=${encodeURIComponent(pn)}` : ''
                        history.push(`${PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_INTRODUCE}${query}`)
                      }}
                    />
                  ) : null}
                </div>
              </SwipeToDeleteRow>
            )
          })}
        </div>
      </div>

      {canToggle ? (
        <button type="button" className="traffic-fine-toggle-btn" onClick={() => setExpanded((v) => !v)}>
          <span>{expanded ? 'Thu gọn' : `Xem thêm${hiddenCount ? ` (${hiddenCount})` : ''}`}</span>
          <span className={`traffic-fine-toggle-arrow ${expanded ? 'is-expanded' : ''}`}>
            <svg viewBox="0 0 12 6" fill="none">
              <path d="M1 1L6 5L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
      ) : null}
    </div>
  )
}
